const initializedDiagrams = new WeakSet();

const getNodeRecords = (diagram) => {
  const nodes = Array.from(diagram.querySelectorAll('[data-company-node]'));
  const recordsById = new Map();

  nodes.forEach((element, index) => {
    const id = element.dataset.companyNodeId || `company-node-${index + 1}`;

    if (!recordsById.has(id)) {
      recordsById.set(id, {
        id,
        element,
        parentId: element.dataset.companyParentId || null,
        children: [],
        depth: 0,
      });
    }
  });

  const records = Array.from(recordsById.values());

  records.forEach((record) => {
    const parent = record.parentId ? recordsById.get(record.parentId) : null;

    if (parent && parent !== record) {
      parent.children.push(record);
    }
  });

  return records;
};

const getRoots = (records) => {
  const roots = records.filter((record) => !record.parentId || !records.some((candidate) => candidate.id === record.parentId));

  return roots.length ? roots : records.slice(0, 1);
};

const setDepths = (roots, records) => {
  const visited = new Set();

  const visit = (record, depth, lineage = new Set()) => {
    if (lineage.has(record.id) || visited.has(record.id)) {
      return;
    }

    visited.add(record.id);
    record.depth = depth;
    const nextLineage = new Set(lineage);
    nextLineage.add(record.id);

    record.children.forEach((child) => visit(child, depth + 1, nextLineage));
  };

  roots.forEach((root) => visit(root, 0));
  records.forEach((record) => {
    if (!visited.has(record.id)) {
      visit(record, 0);
      roots.push(record);
    }
  });
};

const getLeafCount = (roots) => {
  const leaves = [];

  const collectLeaves = (record, lineage = new Set()) => {
    const nextLineage = new Set(lineage);
    nextLineage.add(record.id);
    const children = record.children.filter((child) => !nextLineage.has(child.id));

    if (!children.length) {
      leaves.push(record);
      return;
    }

    children.forEach((child) => collectLeaves(child, nextLineage));
  };

  roots.forEach((root) => collectLeaves(root));

  return Math.max(leaves.length, 1);
};

const getMinimumDesktopWidth = (leafCount) => {
  const nodeWidth = leafCount > 3 ? 200 : 240;

  return Math.max(640, (leafCount * nodeWidth) + ((leafCount - 1) * 24) + 40);
};

const getHorizontalPositioner = (canvasWidth, nodeWidth, leafCount) => {
  const padding = 20;

  if (leafCount === 1) {
    return () => canvasWidth / 2;
  }

  const availableWidth = canvasWidth - (padding * 2) - nodeWidth;
  const step = availableWidth / (leafCount - 1);

  return (index) => padding + (nodeWidth / 2) + (step * index);
};

const getRecordsByDepth = (records) => records.reduce((levels, record) => {
  const level = levels.get(record.depth) || [];
  level.push(record);
  levels.set(record.depth, level);

  return levels;
}, new Map());

const getRowPosition = (canvasWidth, nodeWidth, columns, index) => {
  const padding = 20;

  if (columns === 1) {
    return canvasWidth / 2;
  }

  const availableWidth = canvasWidth - (padding * 2) - nodeWidth;

  return padding + (nodeWidth / 2) + ((availableWidth / (columns - 1)) * index);
};

const getWrappedColumns = (canvasWidth, recordCount) => canvasWidth <= 320 ? 1 : Math.min(recordCount, 2);

const setNodeWidths = (records, canvasWidth, leafCount, shouldWrapRows) => {
  const levels = getRecordsByDepth(records);

  levels.forEach((levelRecords, depth) => {
    const columns = shouldWrapRows ? getWrappedColumns(canvasWidth, levelRecords.length) : Math.max(leafCount, 1);
    const maxWidth = depth === 0 && levelRecords.length === 1
      ? Math.min(240, canvasWidth - 40)
      : Math.min(shouldWrapRows ? 200 : (leafCount > 3 ? 200 : 240), (canvasWidth - 40 - ((columns - 1) * 24)) / columns);

    levelRecords.forEach((record) => {
      record.element.style.width = `${Math.max(maxWidth, 1)}px`;
    });
  });
};

const positionRecords = (roots, leafPosition) => {
  let leafIndex = 0;
  const positioned = new Set();

  const position = (record, lineage = new Set()) => {
    if (positioned.has(record.id) || lineage.has(record.id)) {
      return record.x ?? leafPosition(leafIndex++);
    }

    const nextLineage = new Set(lineage);
    nextLineage.add(record.id);
    const children = record.children.filter((child) => !nextLineage.has(child.id));

    if (!children.length) {
      record.x = leafPosition(leafIndex++);
    } else {
      const childPositions = children.map((child) => position(child, nextLineage));
      record.x = childPositions.reduce((sum, value) => sum + value, 0) / childPositions.length;
    }

    positioned.add(record.id);

    return record.x;
  };

  roots.forEach((root) => position(root));
};

const getLevelMetrics = (records) => {
  const levelHeights = new Map();

  records.forEach((record) => {
    const height = record.element.offsetHeight;
    const currentHeight = levelHeights.get(record.depth) || 0;
    levelHeights.set(record.depth, Math.max(currentHeight, height));
  });

  const levelTops = new Map();
  let currentTop = 0;

  Array.from(levelHeights.keys()).sort((first, second) => first - second).forEach((depth) => {
    levelTops.set(depth, currentTop);
    currentTop += levelHeights.get(depth) + 64;
  });

  return {
    levelTops,
    height: Math.max(currentTop - 64, 0),
  };
};

const positionWrappedRecords = (records, canvasWidth) => {
  const levels = getRecordsByDepth(records);
  let currentTop = 0;

  Array.from(levels.keys()).sort((first, second) => first - second).forEach((depth) => {
    const levelRecords = levels.get(depth);
    const columns = getWrappedColumns(canvasWidth, levelRecords.length);
    const rowCount = Math.ceil(levelRecords.length / columns);
    const rowHeight = Math.max(...levelRecords.map((record) => record.element.offsetHeight));

    levelRecords.forEach((record, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const nodeWidth = record.element.offsetWidth;

      record.x = getRowPosition(canvasWidth, nodeWidth, columns, column);
      record.y = currentTop + (row * (rowHeight + 48));
    });

    currentTop += (rowCount * rowHeight) + ((rowCount - 1) * 48) + 64;
  });

  return Math.max(currentTop - 64, 0);
};

const drawConnectors = (lines, records, hasDrawn) => {
  const namespace = 'http://www.w3.org/2000/svg';

  lines.replaceChildren();

  records.forEach((record) => {
    record.children.forEach((child) => {
      const parentMedia = record.element.querySelector('.company-diagram-sec__media');
      const childMedia = child.element.querySelector('.company-diagram-sec__media');

      if (!parentMedia || !childMedia) {
        return;
      }

      const startX = record.x;
      const startY = record.y + (parentMedia.offsetHeight / 2);
      const endX = child.x;
      const endY = child.y + (childMedia.offsetHeight / 2);
      const line = document.createElementNS(namespace, 'path');

      line.classList.add('company-diagram-sec__line');
      line.setAttribute('d', `M ${startX} ${startY} H ${endX} V ${endY}`);
      lines.append(line);

      const length = line.getTotalLength();
      line.style.strokeDasharray = `${length}`;
      line.style.transition = 'none';
      line.style.strokeDashoffset = hasDrawn ? '0' : `${length}`;
    });
  });
};

const animateConnectors = (lines) => {
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const paths = lines.querySelectorAll('.company-diagram-sec__line');

  void lines.getBoundingClientRect();

  requestAnimationFrame(() => {
    paths.forEach((path, index) => {
      path.style.transition = reducedMotion
        ? 'none'
        : `stroke-dashoffset .7s cubic-bezier(.4, 0, .2, 1) ${index * 90}ms`;
      path.style.strokeDashoffset = '0';
    });
  });
};

const layoutDiagram = (section, state) => {
  const diagram = state.diagram;
  const viewport = state.viewport;
  const canvas = state.canvas;
  const lines = state.lines;
  const records = getNodeRecords(diagram);

  if (!records.length || !viewport || !canvas || !lines) {
    return;
  }

  if (!state.hasRevealed) {
    state.connectorsReady = false;
  }

  const roots = getRoots(records);
  setDepths(roots, records);

  const leafCount = getLeafCount(roots);
  const canvasWidth = viewport.clientWidth;
  const minimumDesktopWidth = getMinimumDesktopWidth(leafCount);
  const shouldWrapRows = canvasWidth < minimumDesktopWidth;

  canvas.style.width = `${canvasWidth}px`;
  setNodeWidths(records, canvasWidth, leafCount, shouldWrapRows);

  let height;

  if (shouldWrapRows) {
    height = positionWrappedRecords(records, canvasWidth);
  } else {
    const nodeWidth = Math.max(...records.map((record) => record.element.offsetWidth), 1);
    const leafPosition = getHorizontalPositioner(canvasWidth, nodeWidth, leafCount);

    positionRecords(roots, leafPosition);

    const metrics = getLevelMetrics(records);
    height = metrics.height;
    records.forEach((record) => {
      record.y = metrics.levelTops.get(record.depth);
    });
  }

  canvas.style.height = `${height}px`;

  records.forEach((record) => {
    record.element.style.setProperty('--company-node-x', `${record.x}px`);
    record.element.style.setProperty('--company-node-y', `${record.y}px`);
  });

  section.classList.add('is-ready');
  section.classList.remove('is-pending');

  if (state.connectorFrame) {
    cancelAnimationFrame(state.connectorFrame);
  }

  state.connectorFrame = requestAnimationFrame(() => {
    state.connectorFrame = null;
    drawConnectors(lines, records, state.hasRevealed);
    state.connectorsReady = true;
    section.classList.add('is-animated');
    state.reveal?.();
  });
};

const initCompanyDiagramSection = (scope = document) => {
  scope.querySelectorAll('.company-diagram-sec').forEach((section) => {
    if (initializedDiagrams.has(section)) {
      return;
    }

    const diagram = section.querySelector('[data-company-diagram]');
    const viewport = section.querySelector('[data-company-diagram-viewport]');
    const canvas = section.querySelector('[data-company-diagram-canvas]');
    const lines = section.querySelector('[data-company-diagram-lines]');

    if (!diagram || !viewport || !canvas || !lines || !diagram.querySelector('[data-company-node]')) {
      return;
    }

    initializedDiagrams.add(section);
    const state = {
      diagram,
      viewport,
      canvas,
      lines,
      frame: null,
      connectorFrame: null,
      connectorsReady: false,
      revealRequested: false,
      hasRevealed: false,
      isRevealing: false,
      relayoutRequested: false,
      reveal: null,
    };
    const scheduleLayout = () => {
      if (state.isRevealing) {
        state.relayoutRequested = true;
        return;
      }

      if (state.frame) {
        cancelAnimationFrame(state.frame);
      }

      state.frame = requestAnimationFrame(() => {
        state.frame = null;

        if (state.isRevealing) {
          state.relayoutRequested = true;
          return;
        }

        layoutDiagram(section, state);
      });
    };
    const revealDiagram = () => {
      state.revealRequested = true;

      if (state.hasRevealed || state.isRevealing || !state.connectorsReady) {
        return;
      }

      state.isRevealing = true;
      requestAnimationFrame(() => {
        section.classList.add('is-drawn');
        animateConnectors(lines);

        window.setTimeout(() => {
          state.hasRevealed = true;
          state.isRevealing = false;

          if (state.relayoutRequested) {
            state.relayoutRequested = false;
            scheduleLayout();
          }
        }, 800);
      });
    };
    state.reveal = revealDiagram;

    scheduleLayout();

    if (document.readyState === 'complete') {
      scheduleLayout();
    } else {
      window.addEventListener('load', scheduleLayout, { once: true });
    }

    document.fonts?.ready.then(scheduleLayout);

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || typeof IntersectionObserver !== 'function') {
      revealDiagram();
    } else {
      const observer = new IntersectionObserver((entries, currentObserver) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            revealDiagram();
            currentObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.25 });

      observer.observe(diagram);
    }

    if (typeof ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(scheduleLayout);
      resizeObserver.observe(viewport);
    } else {
      window.addEventListener('resize', scheduleLayout, { passive: true });
    }
  });
};

initCompanyDiagramSection();
window.initCompanyDiagramSection = initCompanyDiagramSection;
